/* eslint-disable no-nested-ternary */
import { Box, Button, Text, Spinner, Icon } from '@chakra-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { MdPlaylistAddCheck } from 'react-icons/md';
import { useStoreActions, useStoreState } from '../../../../models/hooks';
import { useSubCollectionQuerySnapshot } from '../../../../firebase/firestore/hooks';
import { WhereCriteria } from '../../../../firebase/firestore/query/criteria';
import { FirestoreOperation } from '../../../../firebase/firestore/firestoreOperation';

import { Collections, SubCollections } from '../../../../firebase/firestore/collections';
import { BulkAssignOperations } from '../../../../firebase/firestore/documents/accounts';
import { useQueryFetchStatus } from '../../../../app/hooks/useQueryFetchStatus';

type BulkButtonState = 'idle' | 'active' | 'inProgress';

function useBulkButtonState(isBulkAssignInProgress: boolean, isBulkAssignMode: boolean): BulkButtonState {
  if (isBulkAssignInProgress) return 'inProgress';
  if (isBulkAssignMode) return 'active';
  return 'idle';
}

interface StateConfig {
  label: string;
  sublabel?: string;
  ariaPressed: boolean | 'mixed';
  ariaBusy: boolean;
  isDisabled: boolean;
  colorScheme: string;
  variant: string;
  testId: string;
  leftIcon?: React.ReactElement;
  rightIcon?: React.ReactElement;
}

function getStateConfig(state: BulkButtonState, t: (key: string) => string): StateConfig {
  switch (state) {
    case 'inProgress':
      return {
        label: t('bulk.actionInProgress'),
        ariaPressed: true,
        ariaBusy: true,
        isDisabled: true,
        colorScheme: 'blue',
        variant: 'outline',
        testId: 'bulk-action-inprogress',
        leftIcon: <Spinner size="xs" thickness="2px" speed="0.9s" aria-hidden="true" mt="4px" data-testid="spinner-icon" />,
      };

    case 'active':
      return {
        label: t('bulk.cancelAction'),
        ariaPressed: true,
        ariaBusy: false,
        isDisabled: false,
        colorScheme: 'blue',
        variant: 'solid',
        testId: 'bulk-action-cancel',
      };

    case 'idle':
    default:
      return {
        label: t('bulk.actionButton'),
        ariaPressed: false,
        ariaBusy: false,
        isDisabled: false,
        colorScheme: 'gray',
        variant: 'outline',
        leftIcon: <Icon as={MdPlaylistAddCheck} data-testid="playlist-add-check-icon" boxSize={6} />,
        testId: 'bulk-action',
      };
  }
}

export const BulkAssignButton = React.memo(function BulkAssignButton() {
  const { t } = useTranslation('moniterByRecruiters');

  const { isBulkAssignMode, positionFilters } = useStoreState((state) => state.moniterByRecruiters);
  const { setBulkAssignMode, setPositionFilter, resetSelectedPositions } = useStoreActions((state) => state.moniterByRecruiters);

  const appUserId = useStoreState((s) => s.app.user?.id);
  const owners = useStoreState((s) => s.app.accounts?.owners);
  const superOwners = useStoreState((s) => s.app.accounts?.superOwners);
  const accountId = useStoreState((s) => s.app.accounts?.id);

  // Define the criteria using the FirestoreOperation enum
  const queryObjects = React.useMemo(() => [new WhereCriteria('isBulkAssignInProgress', FirestoreOperation.GREATER, 0)], []);

  //  Query the entire bulkUpdateActions sub-collection for this account
  const bulkUpdateOperations = useSubCollectionQuerySnapshot<BulkAssignOperations>(
    Collections.ACCOUNT,
    accountId || '',
    SubCollections.BULK_UPDATE_ACTIONS,
    queryObjects,
  );

  const { isFetching } = useQueryFetchStatus(bulkUpdateOperations);
  // If the array returns at least 1 document matching the criteria, an operation is in progress
  const isBulkAssignInProgress = React.useMemo(() => {
    if (!bulkUpdateOperations || !appUserId) return false;
    return bulkUpdateOperations.some(
      (op) => op.id === appUserId && (op.isBulkAssignInProgress ?? /* istanbul ignore next */ 0) > 0,
    );
  }, [bulkUpdateOperations, appUserId]);

  const buttonState = useBulkButtonState(isBulkAssignInProgress, isBulkAssignMode);

  const isSuperOwner = React.useMemo(() => {
    if ((!owners && !superOwners) || !appUserId) return false;
    return owners?.includes(appUserId) || superOwners?.includes(appUserId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appUserId, owners, superOwners]);

  const handleClick = React.useCallback(() => {
    // istanbul ignore next
    if (isBulkAssignInProgress) {
      return;
    }
    setBulkAssignMode(!isBulkAssignMode); // Toggles the mode
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBulkAssignMode, setBulkAssignMode, isBulkAssignInProgress]);

  React.useEffect(() => {
    if (isBulkAssignMode) {
      if (positionFilters.limit > 50) {
        setPositionFilter({ key: 'limit', value: 50 });
        setPositionFilter({ key: 'page', value: 1 });
      }
    } else {
      if (positionFilters.limit === 50) {
        setPositionFilter({ key: 'limit', value: 20 });
        setPositionFilter({ key: 'page', value: 1 });
      }
      resetSelectedPositions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBulkAssignMode]);

  if (!isSuperOwner) return null;

  const config = getStateConfig(buttonState, t);
  return (
    <>
      <Box mr={2}>
        <Button
          colorScheme={config.colorScheme}
          variant={config.variant}
          size="sm"
          leftIcon={isFetching ? <Spinner size="xs" thickness="2px" speed="0.9s" aria-hidden="true" mt="4px" /> : config.leftIcon}
          rightIcon={config.rightIcon}
          onClick={handleClick}
          isDisabled={config.isDisabled || isFetching}
          aria-pressed={config.ariaPressed}
          aria-busy={config.ariaBusy || isFetching}
          cursor={config.isDisabled ? 'not-allowed' : 'pointer'}
          data-testid={config.testId}
          data-bulk-state={buttonState}
        >
          <Text lineHeight={2}>{config.label}</Text>
        </Button>
      </Box>
    </>
  );
});
